import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { api, assetUrl } from "@/lib/api";
import PosterCard, { VideoRow, Countdown, PosterTile, DIVISION_META } from "@/components/Studio";
import { NotifyForm } from "@/components/ProductCard";

function PremiereCard({ video }) {
  return (
    <div className="border border-gold/25 bg-coal p-8 flex flex-col sm:flex-row gap-8 items-start" data-testid={`premiere-${video.slug}`}>
      <Link to={`/cinema/watch/${video.slug}`} className="w-full sm:w-64 shrink-0 aspect-video overflow-hidden border border-paper/10 bg-black">
        <PosterTile video={video} />
      </Link>
      <div className="flex-1">
        <p className="eyebrow mb-3">Premiere · {DIVISION_META[video.division]?.title || video.division}</p>
        <h3 className="font-display text-2xl sm:text-3xl text-paper mb-3">{video.title}</h3>
        {video.logline && <p className="font-reading text-lg text-paper/65 mb-6">{video.logline}</p>}
        <Countdown releaseAt={video.release_at} timezone={video.timezone} />
        <div className="mt-6 max-w-md">
          <NotifyForm product={{ product_id: `video:${video.slug}` }} compact />
        </div>
      </div>
    </div>
  );
}

export default function CinemaHome() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/public/cinema").then((r) => setData(r.data)).catch(() => setData({ divisions: {}, premieres: [] }));
  }, []);

  const featured = data?.featured;
  const nextPremiere = data?.premieres?.[0];
  const divisions = data?.divisions || {};

  return (
    <div className="bg-ink min-h-screen" data-testid="cinema-home">
      <Seo title="The Cinema — Dirty Church Chronicles Studios"
        description="Dirty Church Chronicles Studios — official documentaries, short films, trailers, reels, and original series from the world of Dirty Church Chronicles by Jermaine Miller."
        path="/cinema" />

      {/* Studio masthead */}
      <section className="pt-36 pb-10 max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Dirty Church Chronicles Studios</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight" data-testid="cinema-title">
            The Cinema
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl mt-4">
            Truth that confronts. Stories that reveal. Original productions from the world of the Chronicles.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex gap-5 mt-8 overflow-x-auto pb-2" data-testid="division-nav">
            {Object.entries(DIVISION_META).map(([key, meta]) => (
              <Link key={key} to={`/cinema/${key}`} data-testid={`division-link-${key}`}
                className="shrink-0 font-ui text-[11px] uppercase tracking-[0.2em] text-paper/55 border border-paper/15 px-5 py-2.5 rounded-full transition-colors duration-300 hover:border-crimson hover:text-paper">
                {meta.title}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Featured premiere / production */}
      {(featured || nextPremiere) && (
        <section className="relative overflow-hidden border-y border-paper/10" data-testid="cinema-featured">
          {featured?.backdrop_url && (
            <div className="absolute inset-0" aria-hidden="true">
              <img src={assetUrl(featured.backdrop_url)} alt="" className="w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
            </div>
          )}
          <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              {featured ? (
                <Reveal>
                  <p className="eyebrow mb-4">Featured Production</p>
                  <h2 className="font-display font-black text-3xl sm:text-5xl text-paper leading-tight mb-4">{featured.title}</h2>
                  {featured.logline && <p className="font-reading text-xl text-paper/75 mb-8 max-w-xl">{featured.logline}</p>}
                  <div className="flex gap-3 font-ui text-xs text-paper/50 mb-8 flex-wrap">
                    {[DIVISION_META[featured.division]?.title, featured.runtime, featured.release_date].filter(Boolean).map((x, i) => (
                      <span key={i} className="border border-paper/20 px-3 py-1.5 rounded-full">{x}</span>
                    ))}
                  </div>
                  <Link to={`/cinema/watch/${featured.slug}`} data-testid="featured-watch-btn"
                    className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-crimson text-paper px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]">
                    <Play size={15} /> Watch Now
                  </Link>
                </Reveal>
              ) : (
                <Reveal>
                  <p className="eyebrow mb-4">Featured Premiere</p>
                  <h2 className="font-display font-black text-3xl sm:text-5xl text-paper leading-tight mb-4">{nextPremiere.title}</h2>
                  {nextPremiere.logline && <p className="font-reading text-xl text-paper/75 mb-8 max-w-xl">{nextPremiere.logline}</p>}
                  <Countdown releaseAt={nextPremiere.release_at} timezone={nextPremiere.timezone} />
                  <div className="mt-8 max-w-md">
                    <NotifyForm product={{ product_id: `video:${nextPremiere.slug}` }} compact />
                  </div>
                </Reveal>
              )}
            </div>
            <Reveal delay={0.15} className="lg:col-span-5">
              <Link to={`/cinema/watch/${(featured || nextPremiere).slug}`} className="block aspect-video border border-paper/15 overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] bg-black">
                <PosterTile video={featured || nextPremiere} />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Coming soon premieres (beyond the featured one) */}
      {data?.premieres?.length > (featured ? 0 : 1) && (
        <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16" data-testid="coming-soon-row">
          <Reveal><p className="eyebrow mb-8">Coming Soon</p></Reveal>
          <div className="space-y-6">
            {data.premieres.slice(featured ? 0 : 1).map((v) => (
              <Reveal key={v.video_id}><PremiereCard video={v} /></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Division rows */}
      <VideoRow title="Documentaries" videos={divisions["documentaries"]} testid="row-documentaries" />
      <VideoRow title="Original Short Films" videos={divisions["short-films"]} testid="row-short-films" />
      <VideoRow title="Feature Films" videos={divisions["feature-films"]} testid="row-feature-films" />
      <VideoRow title="Original Series" videos={divisions["original-series"]} testid="row-series" />
      <VideoRow title="Latest Trailers" videos={divisions["trailers"]} testid="row-trailers" />
      <VideoRow title="The Chronicles in Motion" videos={data?.in_motion} testid="row-in-motion" />
      <VideoRow title="Short-Form Reels" videos={divisions["reels"]} vertical testid="row-reels" />
      <VideoRow title="Behind the Scenes" videos={divisions["behind-the-scenes"]} testid="row-bts" />

      {/* Empty-studio state */}
      {data && !featured && !nextPremiere && !Object.keys(divisions).length && (
        <section className="max-w-3xl mx-auto px-6 sm:px-10 py-24 text-center" data-testid="cinema-empty">
          <p className="font-display text-2xl text-paper mb-4">The studio is in production.</p>
          <p className="font-ui text-sm text-paper/50">Premieres are announced to the reader list first.</p>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-28 pt-10">
        <Reveal>
          <div className="border border-paper/10 bg-coal p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <div>
              <p className="eyebrow mb-3">The Official Store</p>
              <p className="font-display text-2xl sm:text-3xl text-paper">Own your copy. Wear the word.</p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Link to="/bookstore" data-testid="cinema-bookstore-link"
                className="font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-7 py-4 rounded-full transition-colors duration-300 hover:bg-crimsonhi">
                Official Bookstore
              </Link>
              <Link to="/merch" data-testid="cinema-merch-link"
                className="font-ui text-xs uppercase tracking-[0.22em] text-paper/80 border border-paper/25 px-7 py-4 rounded-full transition-colors duration-300 hover:border-paper/60">
                Shop the Collection
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
